import { TabsOwnProps } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'reduxStore/store'
import { getEstablishmentPatientList } from 'services/establishment'
import { IPatientResponse } from 'types/patient/types'
import { useShowBannerHDA } from 'utils/hda'
import { getFullName, removeAccents } from 'utils/formatString'
import { sortPatients, tabOptions, Tabs, SortOption } from './utils'

interface UsePatientsReturn {
  state: IStateStdTypes
  filter: string
  setFilter: (value: string) => void
  patientList: IPatientResponse[]
  currentList: Tabs
  setCurrentList: (value: Tabs) => void
  activePatients: IPatientResponse[]
  pendingWatchReturnPatients: IPatientResponse[]
  patientLength: number
  selectedIndex: number
  handleChange: TabsOwnProps['onChange']
  notAllowedMonitoring: boolean
  getErrorMessage: () => string
  isHDA: boolean
  sortBy: SortOption
  setSortBy: (value: SortOption) => void
}

export const usePatients = (): UsePatientsReturn => {
  // state
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [filter, setFilter] = useState('')
  const [patientList, setPatientList] = useState<IPatientResponse[]>([])
  const [currentList, setCurrentList] = useState<Tabs>('active')
  const [sortBy, setSortBy] = useState<SortOption>('outdated')

  // Redux selectors
  const establishmentData = useAppSelector(rState => rState.establishment.data)
  const hasPatientMonitoringPermission = useMemo(
    () => establishmentData?.modules_permission?.includes('patient_monitoring'),
    [establishmentData]
  )
  const establishmentId = establishmentData?.establishment_id
  const establishmentName = establishmentData?.commercial_name

  // Computed values
  const notAllowedMonitoring =
    !establishmentId || !hasPatientMonitoringPermission

  const isHDA = useShowBannerHDA()

  const patientListFiltered = useMemo(() => {
    return patientList.filter(p =>
      removeAccents(getFullName(p))
        .toLocaleLowerCase()
        .includes(removeAccents(filter).toLocaleLowerCase())
    )
  }, [patientList, filter])

  const activePatients = useMemo(() => {
    return patientListFiltered.filter(p => {
      // Pacientes sem status são considerados 'active' por padrão
      const patientStatus = p.status || 'active'
      return patientStatus === currentList
    })
  }, [patientListFiltered, currentList])

  const pendingWatchReturnPatients = useMemo(() => {
    return patientListFiltered.filter(p => p.status === 'pending_watch_return')
  }, [patientListFiltered])

  const patientLength = useMemo(() => activePatients.length, [activePatients])

  const selectedIndex = useMemo(
    () => tabOptions.indexOf(currentList),
    [currentList]
  )

  // Helper functions
  const getErrorMessage = () => {
    if (!establishmentId) {
      return 'É necessário estar em uma organização para ver a lista de pacientes'
    }

    if (!hasPatientMonitoringPermission) {
      return 'Você não tem permissão para monitorar pacientes'
    }

    return 'Houve um erro ao buscar seus pacientes!'
  }

  const handleChange: TabsOwnProps['onChange'] = (event, newValue) => {
    setCurrentList(tabOptions[newValue])
  }

  const fetchData = async (isBackgroundFetch = false) => {
    if (establishmentId && hasPatientMonitoringPermission) {
      // Só muda para loading se estivermos no primeiro carregamento
      if (!isBackgroundFetch && state !== 'loading') {
        setState('loading')
      }

      const response = await getEstablishmentPatientList(
        establishmentId,
        isHDA ? currentList : null
      )

      if (response.success && response.data) {
        const patients = sortPatients(response.data, isHDA, sortBy)
        setPatientList(patients)
        // Só muda para success se não for background fetch
        if (!isBackgroundFetch) {
          setState('success')
        }
      } else {
        if (!isBackgroundFetch) {
          setState('error')
        }
      }
    } else {
      if (!isBackgroundFetch) {
        setState('error')
      }
    }
  }

  // Polling effect
  useEffect(() => {
    // initial fetch
    fetchData(false)

    // Set interval for polling with background fetch
    const interval = setInterval(() => {
      fetchData(true)
    }, 60000) // 1 minute

    return () => {
      clearInterval(interval)
    }
  }, [establishmentId, currentList, establishmentName, sortBy])

  return {
    state,
    filter,
    setFilter,
    patientList,
    currentList,
    setCurrentList,
    activePatients,
    pendingWatchReturnPatients,
    patientLength,
    selectedIndex,
    handleChange,
    notAllowedMonitoring,
    getErrorMessage,
    isHDA,
    sortBy,
    setSortBy
  }
}
